import { useState, useRef, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { Check, ChevronDown, PlusIcon } from "lucide-react";
import { ContractType } from "@utils/constants";
import { PositionLevel } from "@utils/constants";
import FileIcon from "@assets/images/file.png";
import { isNullOrEmpty } from "@utils/helperFunction";
import CustomInput from "@components/CustomInput/CustomInput";
import { searchSkill } from "@endpoints/skill.service";
import {
  addAspiration,
  getAspirationDetail,
  updateAspiration,
} from "@endpoints/candidate.service";
import { enqueueSnackbar } from "notistack";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { colors } from "@utils/theme";

const mainCategories = [
  { label: "Education & Training", value: "education" },
  { label: "Engineering", value: "engineering" },
];

const engineeringSubOptions = [
  { label: "All Engineering", value: "all_engineering" },
  { label: "Aerospace Engineering", value: "aerospace" },
  { label: "Automotive Engineering", value: "automotive" },
  { label: "Building Services Engineering", value: "building_services" },
  { label: "Chemical Engineering", value: "chemical" },
  {
    label: "Electrical/Electronic Engineering",
    value: "electrical_electronic",
  },
  {
    label: "Engineering Drafting",
    value: "drafting",
  },
  {
    label: "Enviromental Engineering",
    value: "enviromental",
  },
  {
    label: "Field Engineering",
    value: "field",
  },
  {
    label: "Industrial Engineering",
    value: "industrial",
  },
  {
    label: "Maintenance",
    value: "maintenance",
  },
  {
    label: "Materials Handling Engineering",
    value: "materials_handling",
  },
  {
    label: "Mechanical Engineering",
    value: "mechanical",
  },
  {
    label: "Process Engineering",
    value: "Process",
  },
  {
    label: "Project Engineering",
    value: "project",
  },
  {
    label: "Project Management",
    value: "project_management",
  },
  {
    label: "Supervisors",
    value: "supervisors",
  },
  {
    label: "System Engineering",
    value: "system",
  },
  {
    label: "Water & Waste Engineering",
    value: "water_waste",
  },
  {
    label: "Other",
    value: "other",
  },
];

const positionOptions = [
  { label: "Intern", value: PositionLevel.INTERN },
  { label: "Fresher", value: PositionLevel.FRESHER },
  { label: "Junior", value: PositionLevel.JUNIOR },
  { label: "Associate", value: PositionLevel.ASSOCIATE },
  { label: "Mid", value: PositionLevel.MID },
  { label: "Senior", value: PositionLevel.SENIOR },
  { label: "Lead", value: PositionLevel.LEAD },
  { label: "Manager", value: PositionLevel.MANAGER },
  { label: "Sr. Manager", value: PositionLevel.SR_MANAGER },
  { label: "Director", value: PositionLevel.DIRECTOR },
  { label: "Sr. Director", value: PositionLevel.SR_DIRECTOR },
  { label: "VP", value: PositionLevel.VP },
  { label: "SVP", value: PositionLevel.SVP },
  { label: "EVP", value: PositionLevel.EVP },
  { label: "Founder", value: PositionLevel.FOUNDER },
];

const contractOptions = [
  { label: "Contract", value: ContractType.CONTRACT },
  { label: "Part-Time", value: ContractType.PARTTIME },
  { label: "Permanent", value: ContractType.PERMANENT },
];

const AspirationPage = () => {
  const {
    control,
    setError,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      job_title: "",
      level: "",
      location: "",
      startup: false,
      internship: false,
      industry: "",
      salary: "",
      specific_companies: "",
      contract_type: "",
      skill_1: "",
    },
  });

  const [skills, setSkills] = useState([]);
  const [skillSuggestions, setSkillSuggestions] = useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [hasAspirationProfile, setHasAspirationProfile] = useState(false);
  const [selectedMainCategory, setSelectedMainCategory] = useState(null);
  const [selectedSubOptions, setSelectedSubOptions] = useState([]);
  const [showSubOptions, setShowSubOptions] = useState(false);
  const [showLevelDropdown, setShowLevelDropdown] = useState(false);
  const [showContractDropdown, setShowContractDropdown] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aspirationId, setAspirationId] = useState(null);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);
  const contractDropdownRef = useRef(null);
  const classificationDropdownRef = useRef(null);
  const subDropdownRef = useRef(null);
  const skillInputRef = useRef(null);
  const watchedValues = watch();
  const currentSkillValue = watch("skill_1");

  useEffect(() => {
    getAspirationDetails();
  }, []);

  const getAspirationDetails = async () => {
    try {
      const { data } = await getAspirationDetail();
      if (!data) return;

      setAspirationId(data._id || null);

      reset({
        job_title: data.jobTitle || "",
        level: data.positionLevel || "",
        location: data.location || "",
        startup: data.isStartUp || false,
        internship: data.isInternship || false,
        industry: data.industryType || "",
        salary: data.salary?.min || "",
        specific_companies: data.company || "",
        contract_type: data.contractType || "",
        skill_1: "",
      });

      setSkills(data.skills || []);

      const main = mainCategories.find(
        (cat) => cat.value === data.classification?.main
      );
      setSelectedMainCategory(main || null);

      if (data.classification?.main === "engineering") {
        setShowSubOptions(true);
        const subOptions = engineeringSubOptions.filter((opt) =>
          data.classification.sub.includes(opt.value)
        );
        setSelectedSubOptions(subOptions);
      } else {
        setShowSubOptions(false);
        setSelectedSubOptions([]);
      }

      setHasAspirationProfile(true);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleAspiration = async (payload) => {
    setIsSubmitting(true);
    try {
      const { success } = await addAspiration(payload);
      if (success) {
        enqueueSnackbar("Aspiration profile saved successfully!", {
          variant: "success",
        });
        getAspirationDetails();
        setHasAspirationProfile(true);
        setIsEditing(false);
        return true;
      } else {
        enqueueSnackbar("Failed to save aspiration profile", {
          variant: "error",
        });
        return false;
      }
    } catch (error) {
      console.error("Error saving aspiration:", error);
      enqueueSnackbar(error?.message || "An error occurred while saving", {
        variant: "error",
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const UpdateAspirationDetail = async (payload) => {
    setIsSubmitting(true);
    try {
      const { success } = await updateAspiration(payload);

      if (success) {
        enqueueSnackbar("Aspiration profile saved successfully!", {
          variant: "success",
        });

        setHasAspirationProfile(true);
        setIsEditing(false);
        return true;
      } else {
        enqueueSnackbar("Failed to save aspiration profile", {
          variant: "error",
        });
        return false;
      }
    } catch (error) {
      console.error("Error saving aspiration:", error);
      enqueueSnackbar(error?.message || "An error occurred while saving", {
        variant: "error",
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        classificationDropdownRef.current &&
        !classificationDropdownRef.current.contains(event.target) &&
        subDropdownRef.current &&
        !subDropdownRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
        setShowSubOptions(false);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowLevelDropdown(false);
      }
      if (
        contractDropdownRef.current &&
        !contractDropdownRef.current.contains(event.target)
      ) {
        setShowContractDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleMainCategorySelect = (category) => {
    setSelectedMainCategory(category);
    if (category.value === "engineering") {
      setShowSubOptions(true);
      setSelectedSubOptions([]);
    } else {
      setShowSubOptions(false);
      setSelectedSubOptions([]);
      setIsDropdownOpen(false);
    }
  };

  // Debounce function for skill search
  const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => {
        clearTimeout(handler);
      };
    }, [value, delay]);

    return debouncedValue;
  };

  const debouncedSkillValue = useDebounce(currentSkillValue, 300);

  // Fetch skill suggestions when debounced value changes
  useEffect(() => {
    fetchSkillSuggestions();
  }, [debouncedSkillValue, skills]);

  const fetchSkillSuggestions = async () => {
    if (debouncedSkillValue && debouncedSkillValue.trim().length > 1) {
      setIsLoadingSuggestions(true);
      try {
        const { success, data } = await searchSkill(debouncedSkillValue.trim());

        if (success && data && data.length > 0) {
          setSkillSuggestions(data);
        }
      } catch (error) {
        console.error("Error fetching skill suggestions:", error);
        setSkillSuggestions([]);
      } finally {
        setIsLoadingSuggestions(false);
      }
    } else {
      setSkillSuggestions([]);
      setIsLoadingSuggestions(false);
    }
  };

  const handleAddSkill = () => {
    const newSkill = currentSkillValue?.trim();
    if (
      newSkill &&
      !skills.some(
        (existingSkill) =>
          existingSkill.toLowerCase() === newSkill.toLowerCase()
      )
    ) {
      setSkills((prev) => [...prev, newSkill]);
      setValue("skill_1", "");
    }
  };

  const handleSuggestionClick = (suggestion) => {
    const skillName = suggestion.title || suggestion;

    // Avoid duplicates by checking titles safely
    const isAlreadyAdded = skills.some(
      (existingSkill) =>
        existingSkill.title?.toLowerCase() === skillName.toLowerCase()
    );

    if (!isAlreadyAdded) {
      setSkills((prev) => [...prev, suggestion]); // Push full skill object
    }

    setValue("skill_1", "");
  };

  const handleSubOptionToggle = (subOption) => {
    if (subOption.value === "all_engineering") {
      setSelectedSubOptions([subOption]);
    } else {
      const isSelected = selectedSubOptions.some(
        (item) => item.value === subOption.value
      );
      let newSelection;

      if (isSelected) {
        newSelection = selectedSubOptions.filter(
          (item) => item.value !== subOption.value
        );
      } else {
        newSelection = [
          ...selectedSubOptions.filter(
            (item) => item.value !== "all_engineering"
          ),
          subOption,
        ];
      }

      setSelectedSubOptions(newSelection);
    }
  };

  const handleRemoveSkill = (index) => {
    setSkills((prev) => prev.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && currentSkillValue?.trim()) {
      e.preventDefault();
      handleAddSkill();
    }
  };

  const getDisplayText = () => {
    if (!selectedMainCategory) return "Select preferred classification";

    if (selectedMainCategory.value === "engineering") {
      if (selectedSubOptions.length === 0) return "Engineering";
      if (
        selectedSubOptions.length === 1 &&
        selectedSubOptions[0].value === "all_engineering"
      ) {
        return "All Engineering";
      }
      return `${selectedSubOptions.length} Engineering options selected`;
    }

    return selectedMainCategory.label;
  };

  const onSubmit = async (data) => {
    const payload = {
      jobTitle: data.job_title,
      industryType: data.industry,
      classification: {
        main: selectedMainCategory?.value,
        sub: selectedSubOptions?.map((opt) => opt.value) || [],
      },
      positionLevel: Number(data.level),
      skills: skills.map((skill) => skill._id),
      isStartUp: data.startup,
      isInternship: data.internship,
      company: data.specific_companies,
      location: data.location,
      salary: {
        min: parseInt(data.salary),
        max: parseInt(data.salary),
      },
      contractType: data.contract_type,
    };

    if (aspirationId) {
      await UpdateAspirationDetail({ _id: aspirationId, ...payload });
    } else {
      await handleAspiration(payload);
    }

    // handleAspiration(payload);
    setHasAspirationProfile(true);
    setIsEditing(false);
  };

  const handleCreateClick = () => {
    setIsEditing(true);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSaveClick = () => {
    if (!selectedMainCategory) {
      setError("classification", {
        type: "manual",
        message: "Classification is a required field",
      });
    } else {
      clearErrors("classification");
    }

    if (skills.length <= 0) {
      setError("skill_1", {
        type: "manual",
        message: "Skills is required",
      });
    } else {
      clearErrors("skill_1");
    }

    handleSubmit(onSubmit)();
  };

  return (
    <div className="flex flex-col rounded-lg bg-white w-full  no-scrollbar overflow-y-scroll">
      {loading ? (
        <div className="flex flex-1  items-center justify-center ">
          <LoadingAnimation color={colors.PrimaryBlack} />
        </div>
      ) : (
        <>
          {!hasAspirationProfile && !isEditing ? (
            <div className="flex flex-col justify-center items-center mt-60">
              <div className="text-center">
                <img src={FileIcon} alt="File Icon" className="mx-auto" />
                <p className="text-xs font-medium mb-2">
                  You currently haven't created an aspiration profile.
                </p>
                <p className="font-semibold text-sm mb-2 ">
                  Click here to create one.
                </p>
                <div className=" pl-28 text-center mb-2">
                  <button
                    onClick={handleCreateClick}
                    className="flex items-center justify-center border bg-green-600 rounded-lg px-2 py-1 gap-1  text-white font-semibold text-sm"
                  >
                    <PlusIcon className="w-4 h-4" fill="white" />
                    Create
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <h2 className="text-center text-lg font-medium my-8 mb-5">
                Aspiration Profile
              </h2>
              <div className="flex gap-4 justify-center pl-24">
                {isEditing ? (
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="p-8 max-w-2xl rounded-xl border border-gray-200 w-[60%] mb-12 pl-16"
                  >
                    <div className="space-y-8 pr-20">
                      <CustomInput
                        name="job_title"
                        placeholder="Eg: Marketing manager"
                        label="Job title"
                        control={control}
                        inputClassName="placeholder:text-[#DDDDDD]"
                        errors={errors}
                        rules={{ required: "Job title is required" }}
                      />

                      <div className="w-full">
                        <label className="text-xs mb-0.5 font-medium">
                          Classification
                        </label>
                        <div
                          className="relative mt-1"
                          ref={classificationDropdownRef}
                        >
                          <button
                            type="button"
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            className="w-full rounded-lg border p-2.5 text-sm text-left bg-white flex justify-between items-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                          >
                            <span
                              className={`text-xs m-0.5 font-medium ${
                                !selectedMainCategory
                                  ? "text-[#DDDDDD]"
                                  : "text-gray-900 font-montserrat"
                              }`}
                            >
                              {getDisplayText()}
                            </span>
                            <ChevronDown
                              className={`h-4 w-4 text-gray-400 transition-transform ${
                                isDropdownOpen ? "rotate-180" : ""
                              }`}
                            />
                          </button>

                          {isDropdownOpen && (
                            <div
                              className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg border"
                              ref={subDropdownRef}
                            >
                              {!showSubOptions ? (
                                mainCategories.map((category) => {
                                  const isSelected =
                                    selectedMainCategory?.value ===
                                    category.value;
                                  return (
                                    <div
                                      key={category.value}
                                      onClick={() =>
                                        handleMainCategorySelect(category)
                                      }
                                      className="relative cursor-pointer select-none py-2 pl-10 pr-4 flex items-center hover:bg-blue-100 hover:text-gray-800"
                                    >
                                      <span className="absolute inset-y-0 left-0 flex items-center pl-2">
                                        <div
                                          className={`w-4 h-4 border rounded ${
                                            isSelected
                                              ? "bg-gray-600 border-gray-600"
                                              : "border-gray-300"
                                          } flex items-center justify-center`}
                                        >
                                          {isSelected && (
                                            <Check className="h-3 w-3 text-white" />
                                          )}
                                        </div>
                                      </span>
                                      <span
                                        className={`block truncate ml-2 ${
                                          isSelected
                                            ? "font-medium"
                                            : "font-normal"
                                        }`}
                                      >
                                        {category.label}
                                      </span>
                                    </div>
                                  );
                                })
                              ) : (
                                <>
                                  <div className="px-4 py-2 text-xs font-semibold text-gray-600 bg-gray-50 border-b">
                                    Engineering Categories
                                  </div>
                                  {engineeringSubOptions.map((subOption) => {
                                    const isSelected = selectedSubOptions.some(
                                      (item) => item.value === subOption.value
                                    );
                                    return (
                                      <div
                                        key={subOption.value}
                                        onClick={() =>
                                          handleSubOptionToggle(subOption)
                                        }
                                        className="relative cursor-pointer select-none py-2 pl-10 pr-4 flex items-center hover:bg-gray-100 hover:text-gray-800"
                                      >
                                        <span className="absolute inset-y-0 left-0 flex items-center pl-2">
                                          <div
                                            className={`w-4 h-4 border rounded ${
                                              isSelected
                                                ? "bg-gray-600 border-gray-600"
                                                : "border-gray-300"
                                            } flex items-center justify-center`}
                                          >
                                            {isSelected && (
                                              <Check className="h-3 w-3 text-white" />
                                            )}
                                          </div>
                                        </span>
                                        <span
                                          className={`block truncate ml-2 ${
                                            isSelected
                                              ? "font-medium"
                                              : "font-normal"
                                          }`}
                                        >
                                          {subOption.label}
                                        </span>
                                      </div>
                                    );
                                  })}
                                  <div className="border-t mt-2 pt-2">
                                    <button
                                      type="button"
                                      onClick={() => setShowSubOptions(false)}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-blue-50"
                                    >
                                      ← Back to main categories
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </div>

                        {selectedSubOptions.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {selectedSubOptions.map((option) => (
                              <span
                                key={option.value}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-gray-800"
                              >
                                {option.label}
                                <button
                                  type="button"
                                  onClick={() => handleSubOptionToggle(option)}
                                  className="ml-1 hover:text-gray-600 text-sm"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        )}

                        {errors.classification && (
                          <span className="text-red-500 font-medium text-xs mt-2 block">
                            {errors.classification.message}
                          </span>
                        )}
                      </div>

                      <div className="w-full">
                        <label className="text-xs font-medium mb-2 block text-charcoal">
                          Level
                        </label>

                        <div className="relative" ref={dropdownRef}>
                          <Controller
                            name="level"
                            control={control}
                            rules={{ required: "Level is required" }}
                            render={({ field }) => (
                              <button
                                type="button"
                                onClick={() =>
                                  setShowLevelDropdown((prev) => !prev)
                                }
                                className="w-full rounded-lg border p-2.5 text-sm text-left bg-white flex justify-between items-center hover:border-gray-400"
                              >
                                <span
                                  className={`text-xs mb-0.5 font-medium ${
                                    !field.value
                                      ? "text-[#DDDDDD]"
                                      : "text-gray-900 font-montserrat"
                                  }`}
                                >
                                  {positionOptions.find(
                                    (opt) => opt.value === field.value
                                  )?.label || "Eg. Manager, Director"}
                                </span>
                                <ChevronDown
                                  className={`h-4 w-4 text-gray-400 transition-transform ${
                                    showLevelDropdown ? "rotate-180" : ""
                                  }`}
                                />
                              </button>
                            )}
                          />

                          {showLevelDropdown && (
                            <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg border">
                              {positionOptions.map((option) => (
                                <div
                                  key={option.value}
                                  onClick={() => {
                                    setValue("level", option.value);
                                    setShowLevelDropdown(false);
                                  }}
                                  className={`cursor-pointer select-none py-2 px-4 hover:bg-blue-100 flex items-center justify-between ${
                                    watchedValues.level === option.value
                                      ? "text-gray-700 font-medium"
                                      : ""
                                  }`}
                                >
                                  <span>{option.label}</span>
                                  {watchedValues.level === option.value && (
                                    <Check className="h-4 w-4" />
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {errors.level && (
                          <span className="text-red-500 font-medium text-xs">
                            {errors.level.message}
                          </span>
                        )}
                      </div>

                      <CustomInput
                        name="location"
                        placeholder="Eg: Berlin, Singapore"
                        inputClassName="placeholder:text-[#DDDDDD]"
                        label="Location"
                        control={control}
                        rules={{ required: "Location is required" }}
                        errors={errors}
                      />
                      <div className="space-y-4">
                        <div className="flex items-center gap-28 ">
                          <span className="text-xs font-medium">Startup</span>
                          <Controller
                            name="startup"
                            control={control}
                            render={({ field }) => (
                              <input
                                type="checkbox"
                                checked={field.value}
                                onChange={field.onChange}
                                className="rounded-full accent-gray-800"
                              />
                            )}
                          />
                        </div>

                        <div className="flex items-center gap-24">
                          <span className="text-xs font-medium">
                            Internship
                          </span>
                          <Controller
                            name="internship"
                            control={control}
                            render={({ field }) => (
                              <input
                                type="checkbox"
                                checked={field.value}
                                onChange={field.onChange}
                                className="rounded-full accent-gray-800"
                              />
                            )}
                          />
                        </div>
                      </div>

                      <CustomInput
                        name="industry"
                        placeholder="Enter industry type"
                        inputClassName="placeholder:text-[#DDDDDD]"
                        label="Industry"
                        control={control}
                        rules={{ required: "Industry is required" }}
                        errors={errors}
                      />

                      <CustomInput
                        name="salary"
                        placeholder="$120k - $150k"
                        inputClassName="placeholder:text-[#DDDDDD]"
                        label="Salary expectations"
                        rules={{ required: "Salary is required" }}
                        control={control}
                        errors={errors}
                      />

                      <CustomInput
                        name="specific_companies"
                        inputClassName="placeholder:text-[#DDDDDD]"
                        placeholder="Eg: Google, Shopify"
                        label="Specific companies"
                        rules={{ required: "Companies is required" }}
                        control={control}
                        errors={errors}
                      />

                      <div className="w-full">
                        <label className="text-xs mb-2 font-medium block">
                          Type of contract
                        </label>
                        <div className="relative" ref={contractDropdownRef}>
                          <Controller
                            name="contract_type"
                            control={control}
                            rules={{ required: "Contract is required" }}
                            render={({ field }) => (
                              <button
                                type="button"
                                onClick={() =>
                                  setShowContractDropdown((prev) => !prev)
                                }
                                className="w-full rounded-lg border p-2.5 text-sm text-left bg-white flex justify-between items-center hover:border-gray-400"
                              >
                                <span
                                  className={`text-xs mb-0.5 font-medium ${
                                    !field.value
                                      ? "text-[#DDDDDD]"
                                      : "text-gray-900 font-montserrat"
                                  }`}
                                >
                                  {contractOptions.find(
                                    (opt) => opt.value === field.value
                                  )?.label || "Select type of contract"}
                                </span>
                                <ChevronDown
                                  className={`h-4 w-4 text-gray-400 transition-transform ${
                                    showContractDropdown ? "rotate-180" : ""
                                  }`}
                                />
                              </button>
                            )}
                          />

                          {showContractDropdown && (
                            <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg border">
                              {contractOptions.map((option) => (
                                <div
                                  key={option.value}
                                  onClick={() => {
                                    setValue("contract_type", option.value);
                                    setShowContractDropdown(false);
                                  }}
                                  className={`cursor-pointer select-none py-2 px-4 hover:bg-blue-100 flex items-center justify-between ${
                                    watchedValues.contract_type === option.value
                                      ? "text-gray-700 font-medium"
                                      : ""
                                  }`}
                                >
                                  <span>{option.label}</span>
                                  {watchedValues.contract_type ===
                                    option.value && (
                                    <Check className="h-4 w-4" />
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      {errors.contract_type && (
                        <span className="text-red-500 font-medium text-xs">
                          {errors.contract_type.message}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-col mt-8">
                      <label className="text-xs font-medium mb-2">
                        What skills would you like to be using?
                      </label>

                      <div className="flex items-center gap-3 pr-10 relative">
                        <div className="flex-1 relative" ref={skillInputRef}>
                          <Controller
                            name="skill_1"
                            control={control}
                            // rules={{ required: "Skils is required" }}
                            render={({ field }) => (
                              <input
                                {...field}
                                type="text"
                                placeholder="Eg: JavaScript, Communication, React"
                                className="border w-full p-3 rounded-lg text-xs placeholder:text-[#DDDDDD]"
                                onChange={(e) => {
                                  field.onChange(e.target.value);
                                }}
                                onKeyDown={handleKeyDown}
                                autoComplete="off"
                              />
                            )}
                          />

                          {/* Skill Suggestions Dropdown */}
                          {!isNullOrEmpty(currentSkillValue) && (
                            <div className="absolute z-20 mt-1 max-h-48 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg border">
                              {isLoadingSuggestions ? (
                                <div className="px-4 py-2 text-gray-500">
                                  Loading suggestions...
                                </div>
                              ) : skillSuggestions.length > 0 ? (
                                skillSuggestions.map((suggestion, index) => {
                                  const skillName = suggestion?.title;
                                  return (
                                    <div
                                      key={index}
                                      onClick={() =>
                                        handleSuggestionClick(suggestion)
                                      }
                                      className="cursor-pointer select-none py-2 px-4 hover:bg-blue-100 hover:text-gray-800 text-xs"
                                    >
                                      {skillName}
                                    </div>
                                  );
                                })
                              ) : (
                                <div className="px-4 py-2 text-gray-500 text-xs">
                                  No suggestions found
                                </div>
                              )}
                            </div>
                          )}
                          {errors.skill_1 && (
                            <span className="text-red-500 font-medium text-xs">
                              {errors.skill_1.message}
                            </span>
                          )}
                        </div>

                        <button
                          type="button"
                          onClick={handleAddSkill}
                          className="text-xs font-medium cursor-pointer text-charcoal hover:text-charcoal/80"
                          disabled={!currentSkillValue?.trim()}
                        >
                          Add
                        </button>
                      </div>

                      {skills.length > 0 && (
                        <div className="flex flex-col pr-4 gap-5 mt-5">
                          {skills.map((skill, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-3 w-full"
                            >
                              <div className="flex-1">
                                <input
                                  value={skill?.title}
                                  placeholder="Skill"
                                  readOnly
                                  className="border w-full p-3 rounded-lg text-xs bg-gray-50"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveSkill(index)}
                                className="text-xs font-medium cursor-pointer text-charcoal hover:text-charcoal/80"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </form>
                ) : (
                  <div className="p-8 max-w-2xl rounded-xl border border-gray-200 w-[60%] mb-12 pl-16">
                    <div className="space-y-8 text-sm">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-12">
                        <div className="">
                          <p className="font-bold text-[#6F6F6F] text-xs mb-2">
                            Job title
                          </p>
                          <p className="text-xs">{watchedValues.job_title}</p>
                        </div>
                        <div>
                          <p className="font-bold text-[#6F6F6F] text-xs  mb-2">
                            Classification
                          </p>
                          <p className="text-xs">
                            {selectedMainCategory?.label || "—"}
                            {selectedSubOptions?.length > 0 &&
                              ` (${selectedSubOptions
                                .map((opt) => opt.label)
                                .join(", ")})`}
                          </p>
                        </div>
                        <div>
                          <p className="font-bold text-[#6F6F6F] text-xs  mb-2">
                            Level
                          </p>
                          <p className="text-xs">
                            {positionOptions.find(
                              (opt) => opt.value === watchedValues.level
                            )?.label || "—"}
                          </p>
                        </div>
                        <div>
                          <p className="font-bold text-[#6F6F6F] text-xs  mb-2">
                            Location
                          </p>
                          <p className="text-xs">
                            {watchedValues.location || "—"}
                          </p>
                        </div>
                        <div>
                          <p className="font-bold text-[#6F6F6F] text-xs  mb-2">
                            Startup
                          </p>
                          <p className="text-xs">
                            {watchedValues.startup ? "Yes" : "No"}
                          </p>
                        </div>
                        <div>
                          <p className="font-bold text-[#6F6F6F] text-xs  mb-2">
                            Internship
                          </p>
                          <p className="text-xs">
                            {watchedValues.internship ? "Yes" : "No"}
                          </p>
                        </div>
                        <div>
                          <p className="font-bold text-[#6F6F6F] text-xs  mb-2">
                            Industry
                          </p>
                          <p className="text-xs">
                            {watchedValues.industry || "—"}
                          </p>
                        </div>
                        <div>
                          <p className="font-bold text-[#6F6F6F] text-xs  mb-2">
                            Salary
                          </p>
                          <p className="text-xs">
                            {watchedValues.salary || "—"}
                          </p>
                        </div>
                        <div>
                          <p className="font-bold text-[#6F6F6F] text-xs  mb-2">
                            Specific companies
                          </p>
                          <p className="text-xs">
                            {watchedValues.specific_companies || "—"}
                          </p>
                        </div>
                        <div>
                          <p className="font-bold text-[#6F6F6F] text-xs mb-2">
                            Type of contract
                          </p>
                          <p className="text-xs">
                            {contractOptions.find(
                              (opt) => opt.value === watchedValues.contract_type
                            )?.label || "—"}
                          </p>
                        </div>
                      </div>

                      <div className="">
                        <p className="font-bold text-[#6F6F6F] text-xs mb-2">
                          What skills would you like to be using?
                        </p>
                        <div className="">
                          {skills.length > 0 ? (
                            <div className="flex flex-col  gap-3 mt-3">
                              {skills.map((skill, index) => (
                                <input
                                  key={index}
                                  value={skill?.title}
                                  readOnly
                                  placeholder="Skill"
                                  className="border-[#E9EAF0] w-full p-3  rounded-lg text-xs bg-[#F7F7F7]"
                                />
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500 text-xs mt-2">
                              No skills added
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Save/Edit button */}
                <div className="">
                  <button
                    type="button"
                    onClick={isEditing ? handleSaveClick : handleEditClick}
                    className="hover:bg-primary bg-[#BABABA] text-white text-sm font-bold px-6 py-2 rounded border-[#BABABA] shadow-sm transition-colors"
                  >
                    {isSubmitting ? (
                      <LoadingAnimation className="w-6 h-6 mr-0 -ml-0" />
                    ) : isEditing ? (
                      "Save"
                    ) : (
                      "Edit"
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default AspirationPage;
